// src/components/CommentSection.js
import React, { useState } from 'react';
import Comment from './Comment';
import { useAuth } from '../contexts/AuthContext';

const CommentSection = ({ videoId, comments, onAddComment, loading, error }) => {
  const [newCommentText, setNewCommentText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const { user } = useAuth(); // Get current user

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newCommentText.trim()) {
      setSubmitError('Comment cannot be empty.');
      return;
    }
    if (!videoId) {
        setSubmitError('Cannot add comment: Video ID is missing.');
        return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const success = await onAddComment(videoId, newCommentText);
      if (success) {
        setNewCommentText(''); // Clear input on success
      } else {
        // Error message might be set by the parent component via props,
        // but we can set a generic one here too.
        setSubmitError('Failed to post comment. Please try again.');
      }
    } catch (err) {
      setSubmitError(err.message || 'An error occurred.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mt-8 pt-6 border-t border-gray-200">
      <h3 className="text-2xl font-heading font-bold text-gray-900 mb-4">
        Comments ({comments.length})
      </h3>

      {/* Add Comment Form */}
      <form onSubmit={handleSubmit} className="mb-6">
        <div className="flex items-start space-x-3">
           <div className="flex-shrink-0 w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
            <span className="text-sm font-semibold text-gray-600">
              {(user?.fullName?.charAt(0) || 'U').toUpperCase()}
            </span>
          </div>
          <div className="flex-1">
            <textarea
              value={newCommentText}
              onChange={(e) => setNewCommentText(e.target.value)}
              placeholder="Add your comment..."
              rows="3"
              className="input w-full"
              disabled={isSubmitting}
            />
             {submitError && <p className="text-sm text-red-600 mt-1">{submitError}</p>}
          </div>
        </div>
        <div className="flex justify-end mt-2">
            <button
                type="submit"
                className="btn btn-primary px-5 py-2"
                disabled={isSubmitting || !newCommentText.trim()}
            >
                {isSubmitting ? 'Posting...' : 'Post Comment'}
            </button>
        </div>
      </form>

      {/* Comment List */}
      <div className="space-y-4">
        {loading && <p className="text-gray-600 text-center py-4">Loading comments...</p>}
        {error && !loading && <p className="text-red-600 text-center py-4">{error}</p>}
        {!loading && !error && comments.length === 0 && (
          <p className="text-gray-600 text-center py-4">Be the first to comment!</p>
        )}
        {!loading && !error && comments.length > 0 && (
          comments.map(comment => (
            <Comment key={comment.id} comment={comment} />
          ))
        )}
      </div>
    </div>
  );
};

export default CommentSection;